import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

export enum NotificationMessageType {
  // Host status notifications (from host to API)
  NOTIFICATION_HOST_MODE_ENTERED = 'NOTIFICATION_HOST_MODE_ENTERED',
  NOTIFICATION_SESSION_STARTED = 'NOTIFICATION_SESSION_STARTED',
  NOTIFICATION_SESSION_STOPPED = 'NOTIFICATION_SESSION_STOPPED',
  NOTIFICATION_HOST_MODE_EXITED = 'NOTIFICATION_HOST_MODE_EXITED',

  // Mobile user install notifications
  NOTIFICATION_MOBILE_USER_INSTALL = 'NOTIFICATION_MOBILE_USER_INSTALL',

  // Wishlist dead drop notifications
  NOTIFICATION_WISHLIST_DEADDROP = 'NOTIFICATION_WISHLIST_DEADDROP',

  // Player install email notifications
  NOTIFICATION_PLAYER_INSTALL = 'NOTIFICATION_PLAYER_INSTALL',

  // Soft kick notifications
  NOTIFICATION_SOFT_KICK = 'NOTIFICATION_SOFT_KICK', // From api to player

  // Error notifications
  NOTIFICATION_ERROR = 'NOTIFICATION_ERROR', // From api to any client
  NOTIFICATION_FORTNITE_REMINDER = 'NOTIFICATION_FORTNITE_REMINDER',
}

// Payload schemas
const hostModeEnteredPayloadSchema = z.object({
  hostModeEmail: z.boolean().optional(),
  hostModeSms: z.boolean().optional(),
});

const sessionStartedPayloadSchema = z.object({});

const sessionStoppedPayloadSchema = z.object({});

const hostModeExitedPayloadSchema = z.object({
  hostModeEmail: z.boolean().optional(),
  hostModeSms: z.boolean().optional(),
});

const mobileUserInstallPayloadSchema = z.object({});

const wishlistDeadDropPayloadSchema = z.object({
  campaignId: z.string().optional(), // Optional for backwards compatibility
  tokenJwt: z.string().optional(), // JWT token for user authentication without AUTH_UPGRADE
});

const playerInstallPayloadSchema = z.object({});

const softKickPayloadSchema = z.object({
  reason: z.string().optional(),
  sessionEndsAt: z.number(),
});

const errorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const fortniteReminderPayloadSchema = z.object({
  email: z.string(),
});

// Full message schemas
const hostModeEnteredSchema = baseMessageSchema
  .extend({
    type: z.literal(NotificationMessageType.NOTIFICATION_HOST_MODE_ENTERED),
  })
  .merge(hostModeEnteredPayloadSchema);

const sessionStartedSchema = baseMessageSchema
  .extend({
    type: z.literal(NotificationMessageType.NOTIFICATION_SESSION_STARTED),
  })
  .merge(sessionStartedPayloadSchema);

const sessionStoppedSchema = baseMessageSchema
  .extend({
    type: z.literal(NotificationMessageType.NOTIFICATION_SESSION_STOPPED),
  })
  .merge(sessionStoppedPayloadSchema);

const hostModeExitedSchema = baseMessageSchema
  .extend({
    type: z.literal(NotificationMessageType.NOTIFICATION_HOST_MODE_EXITED),
  })
  .merge(hostModeExitedPayloadSchema);

const mobileUserInstallSchema = baseMessageSchema
  .extend({
    type: z.literal(NotificationMessageType.NOTIFICATION_MOBILE_USER_INSTALL),
  })
  .merge(mobileUserInstallPayloadSchema);

const wishlistDeadDropSchema = baseMessageSchema
  .extend({
    type: z.literal(NotificationMessageType.NOTIFICATION_WISHLIST_DEADDROP),
  })
  .merge(wishlistDeadDropPayloadSchema);

const playerInstallSchema = baseMessageSchema
  .extend({
    type: z.literal(NotificationMessageType.NOTIFICATION_PLAYER_INSTALL),
  })
  .merge(playerInstallPayloadSchema);

const softKickSchema = baseMessageSchema
  .extend({
    type: z.literal(NotificationMessageType.NOTIFICATION_SOFT_KICK),
  })
  .merge(softKickPayloadSchema);

const errorSchema = baseMessageSchema
  .extend({
    type: z.literal(NotificationMessageType.NOTIFICATION_ERROR),
  })
  .merge(errorPayloadSchema);

const fortniteReminderSchema = baseMessageSchema
  .extend({
    type: z.literal(NotificationMessageType.NOTIFICATION_FORTNITE_REMINDER),
  })
  .merge(fortniteReminderPayloadSchema);

export const notificationMessageSchema = z.discriminatedUnion('type', [
  hostModeEnteredSchema,
  sessionStartedSchema,
  sessionStoppedSchema,
  hostModeExitedSchema,
  mobileUserInstallSchema,
  wishlistDeadDropSchema,
  playerInstallSchema,
  softKickSchema,
  errorSchema,
  fortniteReminderSchema,
]);

// Payload types
export type HostModeEnteredPayload = z.infer<typeof hostModeEnteredPayloadSchema>;
export type SessionStartedPayload = z.infer<typeof sessionStartedPayloadSchema>;
export type SessionStoppedPayload = z.infer<typeof sessionStoppedPayloadSchema>;
export type HostModeExitedPayload = z.infer<typeof hostModeExitedPayloadSchema>;
export type MobileUserInstallPayload = z.infer<typeof mobileUserInstallPayloadSchema>;
export type WishlistDeadDropPayload = z.infer<typeof wishlistDeadDropPayloadSchema>;
export type PlayerInstallPayload = z.infer<typeof playerInstallPayloadSchema>;
export type SoftKickPayload = z.infer<typeof softKickPayloadSchema>;
export type ErrorPayload = z.infer<typeof errorPayloadSchema>;
export type FortniteReminderPayload = z.infer<typeof fortniteReminderPayloadSchema>;

// Full message type
export type NotificationMessage = z.infer<typeof notificationMessageSchema>;

type PayloadType<T extends NotificationMessageType> = T extends NotificationMessageType.NOTIFICATION_HOST_MODE_ENTERED
  ? HostModeEnteredPayload
  : T extends NotificationMessageType.NOTIFICATION_SESSION_STARTED
    ? SessionStartedPayload
    : T extends NotificationMessageType.NOTIFICATION_SESSION_STOPPED
      ? SessionStoppedPayload
      : T extends NotificationMessageType.NOTIFICATION_HOST_MODE_EXITED
        ? HostModeExitedPayload
        : T extends NotificationMessageType.NOTIFICATION_MOBILE_USER_INSTALL
          ? MobileUserInstallPayload
          : T extends NotificationMessageType.NOTIFICATION_WISHLIST_DEADDROP
            ? WishlistDeadDropPayload
            : T extends NotificationMessageType.NOTIFICATION_PLAYER_INSTALL
              ? PlayerInstallPayload
              : T extends NotificationMessageType.NOTIFICATION_SOFT_KICK
                ? SoftKickPayload
                : T extends NotificationMessageType.NOTIFICATION_ERROR
                  ? ErrorPayload
                  : T extends NotificationMessageType.NOTIFICATION_FORTNITE_REMINDER
                    ? FortniteReminderPayload
                    : never;

export function generateNotificationMessage<T extends NotificationMessageType>(type: T, payload: PayloadType<T>): NotificationMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case NotificationMessageType.NOTIFICATION_HOST_MODE_ENTERED:
      return hostModeEnteredSchema.parse({
        ...base,
        ...payload,
      });
    case NotificationMessageType.NOTIFICATION_SESSION_STARTED:
      return sessionStartedSchema.parse({
        ...base,
        ...payload,
      });
    case NotificationMessageType.NOTIFICATION_SESSION_STOPPED:
      return sessionStoppedSchema.parse({
        ...base,
        ...payload,
      });
    case NotificationMessageType.NOTIFICATION_HOST_MODE_EXITED:
      return hostModeExitedSchema.parse({
        ...base,
        ...payload,
      });
    case NotificationMessageType.NOTIFICATION_MOBILE_USER_INSTALL:
      return mobileUserInstallSchema.parse({
        ...base,
        ...payload,
      });
    case NotificationMessageType.NOTIFICATION_WISHLIST_DEADDROP:
      return wishlistDeadDropSchema.parse({
        ...base,
        ...payload,
      });
    case NotificationMessageType.NOTIFICATION_PLAYER_INSTALL:
      return playerInstallSchema.parse({
        ...base,
        ...payload,
      });
    case NotificationMessageType.NOTIFICATION_SOFT_KICK:
      return softKickSchema.parse({
        ...base,
        ...payload,
      });
    case NotificationMessageType.NOTIFICATION_ERROR:
      return errorSchema.parse({
        ...base,
        ...payload,
      });
    case NotificationMessageType.NOTIFICATION_FORTNITE_REMINDER:
      return fortniteReminderSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const _exhaustiveCheck: never = type;
      throw new Error(`Unknown notification message type: ${_exhaustiveCheck}`);
    }
  }
}
